import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Check, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { useCurrency } from "@/lib/currency";
import { NetBreakdown } from "@/components/fees/NetBreakdown";
import { FxNote, usdNet } from "@/components/market/FxNote";
import { DurationPicker, PrepaidSummary } from "@/components/market/Prepaid";

export const PLANS = [
  { id: "standard", name: "Standard", apps: 200, eur: 200 },
  { id: "pro", name: "Pro", apps: 500, eur: 400 },
  { id: "ultra", name: "Ultra", apps: 1250, eur: 1000 },
];

// Plan amounts are NET (HT); prices are displayed TTC France (VAT 20 %). Stripe adds the buyer's VAT at checkout.
export const VAT_FR = 0.2;
export const ttc = (eur) => Math.round(eur * (1 + VAT_FR) * 100) / 100;

const formatter = (locale, cur, max) => {
  const f = new Intl.NumberFormat(locale, { style: "currency", currency: cur.toUpperCase(), currencyDisplay: "narrowSymbol", maximumFractionDigits: max, minimumFractionDigits: cur === "usd" && max > 0 ? 2 : 0 });
  return (n) => f.format(n).replace(/ /g, "\u00a0").replace("US$", "$");
};

export const useEuro = () => {
  const { locale } = useI18n();
  return useMemo(() => formatter(locale, "eur", 2), [locale]);
};

const useMoney = () => {
  const { locale } = useI18n();
  return useMemo(() => formatter(locale, "eur", 0), [locale]);
};

export const round5 = (x) => Math.round(x / 5) * 5;

// Display currency: EUR = TTC France; USD = net + tax (sales tax / VAT added by Stripe). `forced` = currency of a running subscription.
export const usePrice = (forced) => {
  const { locale } = useI18n();
  const { usd } = useSite();
  const [chosen, setCur] = useCurrency();
  const cur = usd ? forced || chosen : "eur";
  return useMemo(() => {
    const monthly = (p) => (cur === "usd" ? usd.prices[p.id] ?? usdNet(p.eur, usd.rate) : ttc(p.eur));
    const fmt = formatter(locale, cur, 2);
    return { cur, setCur, usdOn: Boolean(usd) && !forced, fmt, money: formatter(locale, cur, 0), monthly,
      price: (p) => fmt(monthly(p)), k: (key) => (cur === "usd" ? `${key}Usd` : key) };
  }, [locale, cur, usd, forced, setCur]);
};

export const CurrencyToggle = ({ className = "", testId = "currency-toggle" }) => {
  const { t } = useI18n();
  const { cur, setCur, usdOn } = usePrice();
  if (!usdOn) return null;
  return (
    <div role="group" aria-label={t("cur.label")} dir="ltr" className={`inline-flex shrink-0 rounded-full border border-[rgba(245,197,66,0.35)] bg-black/40 p-0.5 text-xs font-bold ${className}`} data-testid={testId}>
      {["eur", "usd"].map((c) => (
        <button key={c} type="button" aria-pressed={cur === c} onClick={() => setCur(c)}
          className={`rounded-full px-3 py-1 transition-colors ${cur === c ? "bg-[var(--gold)] text-black" : "text-neutral-300 hover:text-white"}`} data-testid={`${testId}-${c}`}>
          {c === "eur" ? "EUR €" : "USD $"}
        </button>
      ))}
    </div>
  );
};

const PlansFxNote = () => {
  const { cur } = usePrice();
  return <FxNote show={cur === "usd"} className="max-w-xl sm:text-end" testId="plans-fx-note" />;
};

const parseNum = (s) => {
  const clean = String(s).replace(/[\s\u00a0\u202f€$]/g, "").replace(",", ".");
  return clean && /^\d+(\.\d+)?$/.test(clean) ? Number(clean) : NaN;
};

export const PlanCards = ({ signupBase }) => {
  const { t, locale } = useI18n();
  const { price, k, cur, monthly } = usePrice();
  const [months, setMonths] = useState(1);
  return (
    <div className="min-w-0">
    <div className="mb-3 flex flex-col items-end gap-2"><CurrencyToggle /><PlansFxNote /></div>
    <div className="mb-4 min-w-0"><DurationPicker value={months} onChange={setMonths} testId="plans-duration" /></div>
    <section className="grid min-w-0 gap-4 md:grid-cols-3" data-testid="hosting-offer">
      {PLANS.map((p, i) => (
        <div key={p.id} className={`flex min-w-0 flex-col rounded-3xl p-5 ${i === 1 ? "metal-red" : "card-carbon"}`} data-testid={`plan-card-${p.id}`}>
          <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-[var(--gold-bright)]">{p.name}</p>
          <p className={`mt-2 break-words font-display font-black text-white ${cur === "usd" ? "text-xl sm:text-2xl xl:text-3xl" : "text-2xl sm:text-3xl"}`} data-testid={i === 0 ? "hosting-price" : `plan-price-${p.id}`}>
            {t(k("plans.perMonth"), { price: price(p) }).replace(/\/(?=\p{L})/u, "\u200b/")}
          </p>
          <NetBreakdown ht={cur === "usd" ? monthly(p) : p.eur} currency={cur} className="mt-2" testId={`plan-net-${p.id}`} />
          {months > 1 && <div className="mt-3 rounded-xl bg-black/30 p-3"><PrepaidSummary base={p.eur} months={months} testId={`plan-prepaid-${p.id}`} /></div>}
          <p className="mt-2 text-sm text-neutral-200" data-testid={i === 0 ? "hosting-max-apps" : `plan-apps-${p.id}`}>{t("plans.apps", { n: new Intl.NumberFormat(locale).format(p.apps) })}</p>
          <ul className="mt-3 flex-1 space-y-1 text-xs text-neutral-300">
            {["plans.f1", "plans.f2", "plans.f3"].map((k) => <li key={k} className="flex gap-1.5"><Check className="h-3.5 w-3.5 shrink-0 text-[var(--gold)]" /> {t(k)}</li>)}
          </ul>
          <Link to={`${signupBase}${signupBase.includes("?") ? "&" : "?"}plan=${p.id}`} className="btn-gold btn-cta mt-4 w-full" data-testid={i === 0 ? "hosting-create-space-btn" : `plan-choose-${p.id}`}>
            {t("plans.choose")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </div>
      ))}
    </section>
    <p className="mt-3 text-xs text-neutral-400" data-testid="plans-ttc-note">{t(k("plans.ttcNote"))}</p>
    </div>
  );
};

const ROWS = [["savings.row15", 300000], ["savings.row30", 600000], ["savings.rowRc", 20000, true], ["savings.rowFmo", 2880]];

export const SavingsBlock = () => {
  const { t } = useI18n();
  const money = useMoney();
  const { cur } = usePrice();
  return (
    <section className="metal-red min-w-0 overflow-hidden rounded-3xl p-5 sm:p-8" data-testid="savings-block">
      <h2 className="font-display text-base font-bold text-white md:text-lg">{t("savings.title")}</h2>
      <p className="mt-3 text-sm leading-relaxed text-red-50/90">{t("savings.intro")}</p>
      <p className="mt-4 text-sm font-semibold text-white">{t("savings.example", { amount: money(2000000) })}</p>
      <table className="mt-3 w-full table-fixed text-sm" data-testid="savings-table">
        <thead><tr className="text-start text-xs text-red-100/80"><th className="w-3/5 pb-2 text-start font-normal" /><th className="pb-2 text-end font-semibold">{t("savings.colPay")}</th></tr></thead>
        <tbody>
          {ROWS.map(([k, n, plus]) => (
            <tr key={k} className={`border-t border-white/10 ${k === "savings.rowFmo" ? "text-[var(--gold-bright)] font-bold" : "text-white"}`} data-testid={`savings-${k.split(".")[1]}`}>
              <td className="break-words py-2 pe-2">{t(k)}</td>
              <td className="py-2 text-end tabular-nums">{plus ? "+ " : ""}{money(n)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-4 font-display text-sm font-bold text-[var(--gold-bright)] md:text-base" data-testid="savings-total">{t("savings.total", { amount: money(597120) })}</p>
      <p className="mt-3 text-[0.7rem] leading-relaxed text-red-100/70">{t("savings.note")}</p>
      {cur === "usd" && (
        <a href="#savings-calculator" className="mt-3 block text-xs font-semibold text-[var(--gold-bright)] underline-offset-4 hover:underline" data-testid="savings-eur-note">
          {t("savings.eurNote")}
        </a>
      )}
    </section>
  );
};

export function compute(revenue, apps, monthly = (p) => ttc(p.eur)) {
  const rc = revenue > 30000 ? revenue * 0.01 : 0;
  const base30 = revenue * 0.3 + rc;
  const base15 = revenue * 0.15 + rc;
  const rows = PLANS.map((p) => {
    const cost = monthly(p) * 12;
    const fits = apps <= p.apps;
    return { ...p, cost, fits, save30: base30 - cost, rate30: base30 > 0 ? (base30 - cost) / base30 : 0,
      save15: base15 - cost, rate15: base15 > 0 ? (base15 - cost) / base15 : 0 };
  });
  return { rc, base30, base15, rows, recommended: rows.find((r) => r.fits)?.id || null, tooMany: apps > 1250 };
}

const SaveLine = ({ save, rate, label, testId }) => {
  const { t, locale } = useI18n();
  const { money } = usePrice();
  if (save <= 0) return <p className="text-xs text-neutral-400" data-testid={testId}>{label} : {t("calc.noSaving")}</p>;
  const pct = new Intl.NumberFormat(locale, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rate);
  return <p className="text-xs text-neutral-200" data-testid={testId}>{label} : <span className="font-semibold text-[var(--turquoise,#3fd6e0)]">{t("calc.saves", { amount: money(save) })}</span> <span className="text-[#e86ad8]">({pct})</span></p>;
};

export const SavingsCalculator = () => {
  const { t, locale } = useI18n();
  const { money, monthly, k, cur } = usePrice();
  const [rev, setRev] = useState("2000000");
  const [apps, setApps] = useState("200");
  const r = parseNum(rev);
  const a = parseNum(apps);
  const valid = Number.isFinite(r) && r >= 0 && Number.isInteger(a) && a >= 1;
  const res = valid ? compute(r, a, monthly) : null;
  return (
    <section id="savings-calculator" className="card-carbon min-w-0 scroll-mt-24 rounded-3xl border border-[rgba(245,197,66,0.35)] p-5 sm:p-8" data-testid="savings-calculator">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="flex min-w-0 items-start gap-2 font-display text-sm font-bold text-white md:text-base"><Calculator className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold)]" /> {t("calc.title")}</h2>
        <CurrencyToggle testId="calc-currency-toggle" />
        <FxNote show={cur === "usd"} className="basis-full" testId="calc-fx-note" />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="min-w-0 text-xs text-neutral-400">{t(k("calc.revenue"))}
          <Input inputMode="decimal" value={rev} onChange={(e) => setRev(e.target.value)} className="mt-1" data-testid="calc-revenue-input" />
        </label>
        <label className="min-w-0 text-xs text-neutral-400">{t("calc.apps")}
          <Input inputMode="numeric" value={apps} onChange={(e) => setApps(e.target.value)} className="mt-1" data-testid="calc-apps-input" />
        </label>
      </div>
      {!valid && <p className="mt-3 text-sm text-red-300" role="alert" data-testid="calc-invalid">{t("calc.invalid")}</p>}
      {res && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-neutral-400" data-testid="calc-base">
            {t("calc.base30", { amount: money(res.base30) })} · {t("calc.base15", { amount: money(res.base15) })}
            {res.rc ? ` · ${t("calc.rcIncluded", { amount: money(res.rc) })}` : ` · ${t(k("calc.rcNone"))}`}
          </p>
          {res.tooMany && (
            <Link to="/espace-developpeur/messages?nouveau=custom" onClick={() => sessionStorage.setItem("fmo_after_login", "/espace-developpeur/messages?nouveau=custom")}
              className="block rounded-xl border border-[var(--gold)] p-3 text-sm text-[var(--gold-bright)] underline-offset-4 hover:underline" data-testid="calc-contact">{t("calc.contact")}</Link>
          )}
          <ul className="grid gap-3 md:grid-cols-3" data-testid="calc-plans">
            {res.rows.map((p) => (
              <li key={p.id} className={`min-w-0 rounded-2xl border p-4 ${!p.fits ? "border-neutral-800 opacity-50" : p.id === res.recommended ? "border-[var(--gold)] bg-[rgba(245,197,66,0.08)]" : "border-[rgba(245,197,66,0.2)]"}`} data-testid={`calc-plan-${p.id}`}>
                <p className="flex flex-wrap items-center gap-2 font-display text-sm font-bold text-white">{p.name}
                  {p.id === res.recommended && <span className="rounded-full bg-[var(--gold)] px-2 py-0.5 text-[0.65rem] text-black" data-testid="calc-recommended">{t("calc.recommended")}</span>}
                </p>
                <p className="mt-1 text-xs text-neutral-400">{t(k("calc.yearly"), { amount: money(p.cost), n: new Intl.NumberFormat(locale).format(p.apps) })}</p>
                {!p.fits ? <p className="mt-2 text-xs text-neutral-400" data-testid={`calc-unfit-${p.id}`}>{t("calc.unfit", { max: new Intl.NumberFormat(locale).format(p.apps) })}</p> : (
                  <div className="mt-2 space-y-1">
                    <SaveLine save={p.save30} rate={p.rate30} label={t("calc.vs30")} testId={`calc-save30-${p.id}`} />
                    <SaveLine save={p.save15} rate={p.rate15} label={t("calc.vs15")} testId={`calc-save15-${p.id}`} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export const AndroidVerifyReminder = ({ className = "" }) => {
  const { t } = useI18n();
  return (
    <p className={`flex items-start gap-2 rounded-2xl border border-[rgba(63,214,224,0.4)] bg-[rgba(63,214,224,0.06)] p-4 text-sm text-neutral-200 ${className}`} data-testid="android-verify-reminder">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3fd6e0]" />
      <span>{t("verify.reminder")} <a href="https://developer.android.com/developer-verification" target="_blank" rel="noopener noreferrer" className="text-[var(--gold)] underline">{t("verify.learn")}</a></span>
    </p>
  );
};
